import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExternalLinkAlt, faHeart, faHeartCrack,
  faComment, faCircleInfo, faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

var API_BASE    = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';
var STATIC_BASE = API_BASE.replace('/api/v1', '');

/* ── Resolve full image URL ───────────────────────────────────── */
function imgSrc(coverImage) {
  if (!coverImage) return null;
  if (coverImage.startsWith('blob:') || coverImage.startsWith('http')) return coverImage;
  // Ensure exactly one slash between base and path
  var path = coverImage.startsWith('/') ? coverImage : '/' + coverImage;
  return STATIC_BASE + path;
}

/* ── Fingerprint ─────────────────────────────────────────────── */
function getFingerprint() {
  var key = 'jce_fp';
  var fp  = localStorage.getItem(key);
  if (!fp) {
    fp = Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(key, fp);
  }
  return fp;
}

var ProjectCard = function(props) {
  var id          = props.id;
  var title       = props.title;
  var description = props.description;
  var viewLink    = props.viewLink;
  var coverImage  = props.coverImage;
  var tags        = props.tags || [];

  var likesState      = useState(0);     var likes        = likesState[0];      var setLikes        = likesState[1];
  var dislikesState   = useState(0);     var dislikes     = dislikesState[0];   var setDislikes     = dislikesState[1];
  var commentsState   = useState([]);    var comments     = commentsState[0];   var setComments     = commentsState[1];
  var reactedState    = useState(null);  var reacted      = reactedState[0];    var setReacted      = reactedState[1];
  var newCommentState = useState('');    var newComment   = newCommentState[0]; var setNewComment   = newCommentState[1];
  var showState       = useState(false); var showComments = showState[0];       var setShowComments = showState[1];
  var loadingState    = useState(true);  var loading      = loadingState[0];    var setLoading      = loadingState[1];
  var submittingState = useState(false); var submitting   = submittingState[0]; var setSubmitting   = submittingState[1];
  var commentMsgState = useState('');    var commentMsg   = commentMsgState[0]; var setCommentMsg   = commentMsgState[1];
  var nameState       = useState('');    var authorName   = nameState[0];       var setAuthorName   = nameState[1];
  var emailState      = useState('');    var authorEmail  = emailState[0];      var setAuthorEmail  = emailState[1];

  useEffect(function() {
    var fp = getFingerprint();
    Promise.all([
      fetch(API_BASE + '/projects/' + id),
      fetch(API_BASE + '/projects/' + id + '/comments'),
      fetch(API_BASE + '/projects/' + id + '/reaction-status?fingerprint=' + fp),
    ])
      .then(function(responses) {
        return Promise.all(responses.map(function(r) { return r.json(); }));
      })
      .then(function(data) {
        var project     = data[0];
        var commentList = data[1];
        var status      = data[2];
        if (project && !project.statusCode) {
          setLikes(project.likeCount    || 0);
          setDislikes(project.dislikeCount || 0);
        }
        setComments(Array.isArray(commentList) ? commentList : []);
        setReacted((status && status.reacted) || null);
      })
      .catch(function(e) { console.error('ProjectCard fetch error:', e); })
      .finally(function() { setLoading(false); });
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleReact(type) {
    if (reacted) return;
    var fp = getFingerprint();
    fetch(API_BASE + '/projects/' + id + '/react', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: type, fingerprint: fp }),
    })
      .then(function(r) { return r.json(); })
      .then(function(d) {
        if (d.reacted) { setReacted(d.reacted); setLikes(d.likeCount); setDislikes(d.dislikeCount); }
      })
      .catch(function(e) { console.error(e); });
  }

  function handleCommentSubmit() {
    if (!newComment.trim() || !authorName.trim()) {
      setCommentMsg('error:Please fill in your name and comment.');
      return;
    }
    setSubmitting(true); setCommentMsg('');
    fetch(API_BASE + '/projects/' + id + '/comments', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ authorName: authorName, authorEmail: authorEmail, body: newComment }),
    })
      .then(function(r) { return r.json(); })
      .then(function() {
        setCommentMsg('success:Thank you! Your comment is awaiting moderation.');
        setNewComment(''); setAuthorName(''); setAuthorEmail('');
      })
      .catch(function() { setCommentMsg('error:Something went wrong. Please try again.'); })
      .finally(function() { setSubmitting(false); });
  }

  var resolvedSrc = imgSrc(coverImage);
  var isLiked     = reacted === 'like';
  var isDisliked  = reacted === 'dislike';

  return (
    <motion.article
      className="pcard"
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      whileHover={{ y: -6, transition: { duration: 0.25 } }}
      aria-label={title}
    >
      {/* Cover image */}
      {resolvedSrc ? (
        <div className="pcard__cover">
          <img
            src={resolvedSrc}
            alt={title}
            className="pcard__cover-img"
            onError={function(e) { e.target.parentNode.style.display = 'none'; }}
          />
        </div>
      ) : (
        <div className="pcard__cover pcard__cover--placeholder">
          <span className="pcard__cover-num">{String(id).padStart(2, '0')}</span>
        </div>
      )}

      {/* Header row */}
      <div className="pcard__header">
        <span className="pcard__num" aria-hidden="true">{String(id).padStart(2, '0')}</span>
        {loading && <FontAwesomeIcon icon={faSpinner} spin style={{ color: 'var(--clr-text-subtle)', fontSize: '.75rem' }} />}
        <Link to={'/projects/' + id} className="pcard__info-btn" aria-label={'Details for ' + title}>
          <FontAwesomeIcon icon={faCircleInfo} />
        </Link>
      </div>

      <h3 className="pcard__title">{title}</h3>
      <p className="pcard__desc">{description}</p>

      {tags.length > 0 && (
        <div className="pcard__tags">
          {tags.map(function(tag) { return <span key={tag} className="pcard__tag">{tag}</span>; })}
        </div>
      )}

      <div className="pcard__actions">
        {viewLink && (
          <a href={viewLink} className="pcard__view-btn" target="_blank" rel="noopener noreferrer">
            View Project <FontAwesomeIcon icon={faExternalLinkAlt} />
          </a>
        )}
        <div className="pcard__reactions">
          <button className={'pcard__react-btn' + (isLiked ? ' active-like' : '')}
            onClick={function() { handleReact('like'); }} disabled={Boolean(reacted)}
            aria-label={'Like — ' + likes} aria-pressed={isLiked}
            title={reacted ? 'You already reacted' : 'Like this project'}>
            <FontAwesomeIcon icon={faHeart} /><span>{likes}</span>
          </button>
          <button className={'pcard__react-btn pcard__react-btn--dis' + (isDisliked ? ' active-dislike' : '')}
            onClick={function() { handleReact('dislike'); }} disabled={Boolean(reacted)}
            aria-label={'Dislike — ' + dislikes} aria-pressed={isDisliked}
            title={reacted ? 'You already reacted' : 'Dislike this project'}>
            <FontAwesomeIcon icon={faHeartCrack} /><span>{dislikes}</span>
          </button>
          <button className={'pcard__react-btn' + (showComments ? ' active-comment' : '')}
            onClick={function() { setShowComments(function(v) { return !v; }); }}
            aria-label={'Comments — ' + comments.length} aria-expanded={showComments}>
            <FontAwesomeIcon icon={faComment} /><span>{comments.length}</span>
          </button>
        </div>
      </div>

      {/* Comments drawer */}
      <AnimatePresence>
        {showComments && (
          <motion.div className="pcard__comments"
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}>
            <div className="pcard__comments-inner">
              <p className="pcard__comments-label">Comments <span>({comments.length})</span></p>
              {comments.length === 0
                ? <p className="pcard__no-comments">No comments yet. Be the first!</p>
                : (
                  <div className="pcard__comment-list">
                    {comments.map(function(c, i) {
                      return (
                        <div key={i} className="pcard__comment-item">
                          <span className="pcard__comment-author">{c.authorName || 'Guest'}</span>
                          <p className="pcard__comment-text">{c.body}</p>
                        </div>
                      );
                    })}
                  </div>
                )
              }
              <div className="pcard__comment-form">
                {commentMsg && (
                  <div className={'pcard__comment-msg pcard__comment-msg--' + (commentMsg.startsWith('success') ? 'success' : 'error')}>
                    {commentMsg.replace(/^(success|error):/, '')}
                  </div>
                )}
                <div className="pcard__form-row">
                  <input className="pcard__input" type="text" placeholder="Your name *"
                    value={authorName} onChange={function(e) { setAuthorName(e.target.value); }} />
                  <input className="pcard__input" type="email" placeholder="Email (optional)"
                    value={authorEmail} onChange={function(e) { setAuthorEmail(e.target.value); }} />
                </div>
                <textarea className="pcard__textarea" placeholder="Write a comment…"
                  value={newComment} onChange={function(e) { setNewComment(e.target.value); }}
                  rows={3} aria-label="Write a comment" />
                <button className="pcard__submit-btn" onClick={handleCommentSubmit} disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Post Comment'}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.article>
  );
};

export default ProjectCard;